import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSSEJobMonitoring from '../../hooks/useSSEJobMonitoring';
import { apiCall } from '../../services/api.service';
import ActiveProcessesRegistry from '../../utils/activeProcessesRegistry';
import { toastError, toastInfo, toastSuccess } from '../clu/utils/notify';
import type { JobCenterJob, JobCenterState, JobV5Status } from './jobCenter.types';
import { JobCenterStorage } from './jobCenter.storage';

export type RegisterJobInput = {
  job_id: string;
  title: string;
  job_type?: string;
  agent_id?: number | null;
  origin?: string;
  auto_open?: boolean; // default true
};

type JobCenterApi = {
  state: JobCenterState;
  jobs: JobCenterJob[];
  activeJobs: JobCenterJob[];
  registerJob: (input: RegisterJobInput) => void;
  dismissJob: (job_id: string) => void;
  open: (reason?: string) => void;
  close: (userInitiated?: boolean) => void;
  getJob: (job_id: string) => JobCenterJob | null;
};

const JobCenterContext = createContext<JobCenterApi | null>(null);

export function JobCenterProvider({ children }: { children: React.ReactNode }) {
  const { startSSEMonitoring, stopSSEMonitoring, activeJobs: sseJobs } = useSSEJobMonitoring();

  const [state, setState] = useState<JobCenterState>(() => JobCenterStorage.prune(JobCenterStorage.load()));

  // Polling fallback timers keyed by job_id
  const pollersRef = useRef<Map<string, number>>(new Map());
  // Consecutive 401/403 counter keyed by job_id (transient auth failures on boot)
  const unauthCountRef = useRef<Map<string, number>>(new Map());
  // Auto-dismiss timers for terminal jobs
  const autoDismissRef = useRef<Map<string, number>>(new Map());
  // Dismissed job IDs — persisted to localStorage so they survive page refresh
  const dismissedJobIds = useRef<Set<string>>(JobCenterStorage.loadDismissed());

  // Persist on changes (best-effort)
  useEffect(() => {
    JobCenterStorage.save(JobCenterStorage.prune(state));
  }, [state]);

  // Rehydrate monitoring on boot
  useEffect(() => {
    const pruned = JobCenterStorage.prune(JobCenterStorage.load());
    setState(pruned);

    // Sync all jobs with backend to ensure we have latest state
    const syncJobWithBackend = async (jobId: string) => {
      try {
        const status = await apiCall(`jobs/v5/status/${jobId}`, 'GET', null, false, { silentError: true });
        const st = normalizeStatus(status?.status || status?.state?.status);
        const progress = clampProgress(status?.progress ?? status?.state?.progress);
        const step = (status?.step || status?.state?.step) ?? null;
        const message = (status?.message || status?.state?.message) ?? null;
        const metadata = status?.metadata || status?.state?.metadata || null;
        const result = status?.result || status?.state?.result || null; // ✅ CRITICAL: Preserve result for downloads
        const noResult = status?.execution_result_available === false;
        const created =
          status?.created_at ?? status?.state?.created_at ?? metadata?.created_at;
        const ageMin = created ? (Date.now() - new Date(created).getTime()) / 60000 : 0;

        setState((prev) => {
          const j = prev.jobs[jobId];
          if (!j) return prev;
          // Stop tracking stale "created" / "pending_retry" jobs with no result (avoids poll loop)
          if ((st === 'created' || st === 'pending_retry') && noResult && ageMin > 15) {
            const next = { ...prev, jobs: { ...prev.jobs } };
            delete next.jobs[jobId];
            return next;
          }
          const nextJob: JobCenterJob = {
            ...j,
            status: st,
            progress,
            step: typeof step === 'string' ? step : j.step,
            message: typeof message === 'string' ? message : j.message,
            last_updated_at: new Date().toISOString(),
            // Preserve result/metadata from backend if available (CRITICAL for PDF downloads after refresh)
            result: result || metadata || j.result,
          };
          return { ...prev, jobs: { ...prev.jobs, [jobId]: nextJob } };
        });
      } catch (e) {
        // Silent fail - job might not exist in backend anymore
        console.debug(`Could not sync job ${jobId} with backend:`, e);
      }
    };

    // CRITICAL: Query backend for active jobs to ensure persistence guarantees visibility
    // This ensures jobs persisted in PostgreSQL always appear in Job Center, even after refresh
    const discoverActiveJobsFromBackend = async () => {
      try {
        const activeJobs = await apiCall('jobs/v5/active', 'GET', null, false, { silentError: true });
        
        if (!activeJobs || !Array.isArray(activeJobs)) {
          return;
        }

        // Prune dismissed set: remove IDs that are no longer in the backend
        const activeJobIds = new Set<string>(
          activeJobs.map((j: any) => j.job_id || j.id).filter(Boolean)
        );
        const prunedDismissed = JobCenterStorage.pruneDismissed(dismissedJobIds.current, activeJobIds);
        dismissedJobIds.current = prunedDismissed;
        JobCenterStorage.saveDismissed(prunedDismissed);

        // Register any active jobs that aren't already in localStorage
        for (const backendJob of activeJobs) {
          const jobId = backendJob.job_id || backendJob.id;
          if (!jobId) continue;

          // Skip jobs the user explicitly dismissed — they should not reappear on refresh
          if (dismissedJobIds.current.has(jobId)) continue;

          const jobType = backendJob.job_type || backendJob.metadata?.job_type || 'unknown';
          const status = normalizeStatus(backendJob.status || 'created');
          
          // Register non-terminal jobs (persistence guarantee for active jobs)
          // Also register recently completed jobs (within last hour) for PDF downloads after refresh
          // The backend already filters completed jobs to only include recent ones (within 1 hour)
          const isActive = !isTerminal(status);
          const isRecentlyCompleted = status === 'completed';
          
          if (!isActive && !isRecentlyCompleted) {
            // Skip old completed/failed jobs (backend already filters these)
            continue;
          }

          // Check if job is already registered
          const existing = pruned.jobs[jobId];
          if (existing) {
            // Update existing job with backend state
            syncJobWithBackend(jobId);
            continue;
          }

          // Register new job from backend (PERSISTENCE GUARANTEE)
          // This ensures jobs in PostgreSQL always appear in Job Center
          const title = backendJob.title || 
                       backendJob.metadata?.title || 
                       backendJob.message ||
                       `${jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
          
          const nowIso = new Date().toISOString();
          // Helper function to clamp progress (same as clampProgress but inline to avoid dependency)
          const progress = (() => {
            const n = Number(backendJob.progress);
            if (!Number.isFinite(n)) return 0;
            return Math.max(0, Math.min(100, Math.round(n)));
          })();
          
          // ✅ CRITICAL: Preserve result for completed jobs (needed for PDF downloads after refresh)
          const result = backendJob.result || backendJob.metadata?.result || null;
          
          // Use setState directly to add job (same as registerJob but without toast)
          setState((prev) => {
            const next: JobCenterState = {
              ...prev,
              jobs: {
                ...prev.jobs,
                [jobId]: {
                  job_id: jobId,
                  title,
                  job_type: jobType,
                  agent_id: backendJob.agent_id || backendJob.metadata?.agent_id || null,
                  origin: backendJob.origin || backendJob.metadata?.origin || window.location.pathname,
                  status: status,
                  progress: progress,
                  step: backendJob.step || 'created',
                  message: backendJob.message || 'Job discovered from backend',
                  created_at: backendJob.created_at || nowIso,
                  last_updated_at: backendJob.updated_at || nowIso,
                  transport: 'sse',
                  result: result, // ✅ Preserve result for completed jobs
                },
              },
            };
            return JobCenterStorage.prune(next);
          });

          // Start tracking immediately (only for active jobs)
          if (isActive) {
            ActiveProcessesRegistry.register(jobId, jobType, { title, origin: 'rehydration' });
            startTracking(jobId, { silent: true });
          }
          // For completed jobs, don't start tracking (they're already done)
          // But they'll be visible in Job Center for download
        }
      } catch (e) {
        console.warn('Could not discover active jobs from backend:', e);
        // Continue with localStorage jobs even if backend query fails
      }
    };

    // First, discover active jobs from backend (persistence guarantee)
    discoverActiveJobsFromBackend();

    // Then sync existing localStorage jobs
    for (const job of Object.values(pruned.jobs)) {
      if (!isTerminal(job.status)) {
        // Skip stale zombie jobs (created/unknown at 0% and older than 10 min)
        const jobAgeMs = Date.now() - (Date.parse(job.created_at) || Date.now());
        const isStaleZombie = (job.status === 'created' || job.status === 'unknown') && job.progress === 0 && jobAgeMs > 10 * 60 * 1000;
        // Skip jobs already at 100% — they just need a final status sync, not SSE
        const isEffectivelyDone = job.progress >= 100;

        if (isStaleZombie) {
          setState((prev) => {
            const nextJobs = { ...prev.jobs };
            delete nextJobs[job.job_id];
            return { ...prev, jobs: nextJobs };
          });
        } else if (isEffectivelyDone) {
          syncJobWithBackend(job.job_id);
        } else {
          ActiveProcessesRegistry.register(job.job_id, job.job_type || 'unknown', { title: job.title, origin: 'rehydration' });
          startTracking(job.job_id, { silent: true });
        }
      } else {
        syncJobWithBackend(job.job_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge SSE connection info into registry jobs (and decide polling fallback)
  useEffect(() => {
    // sseJobs is a Map(jobId -> state)
    if (!sseJobs || sseJobs.size === 0) return;

    // Track which jobs need cleanup to avoid calling stopSSEMonitoring during state update
    const jobsToCleanup: string[] = [];

    setState((prev) => {
      const next: JobCenterState = { ...prev, jobs: { ...prev.jobs } };
      let hasChanges = false;

      for (const [jobId, sseState] of sseJobs.entries()) {
        const existing = next.jobs[jobId];
        if (!existing) continue;

        const status = normalizeStatus(sseState?.status);
        const progress = clampProgress(sseState?.progress);
        const step = typeof sseState?.step === 'string' ? sseState.step : existing.step;
        const message = typeof sseState?.message === 'string' ? sseState.message : existing.message;

        // Check if there are actual changes before updating
        const statusChanged = status !== existing.status;
        const progressChanged = progress !== existing.progress;
        const stepChanged = step !== existing.step;
        const messageChanged = message !== existing.message;
        const connectionStatusChanged = (sseState?.connectionStatus || 'unknown') !== existing.connection_status;
        const errorCountChanged = (Number.isFinite(sseState?.errorCount) ? Number(sseState.errorCount) : existing.error_count) !== existing.error_count;
        const resultChanged = (isTerminal(status) && sseState?.result !== undefined) ? sseState.result !== existing.result : false;

        if (!statusChanged && !progressChanged && !stepChanged && !messageChanged && !connectionStatusChanged && !errorCountChanged && !resultChanged) {
          continue; // Skip if no changes
        }

        hasChanges = true;

        next.jobs[jobId] = {
          ...existing,
          status,
          progress,
          step,
          message,
          transport: 'sse',
          connection_status: sseState?.connectionStatus || existing.connection_status || 'unknown',
          error_count: Number.isFinite(sseState?.errorCount) ? Number(sseState.errorCount) : existing.error_count,
          last_updated_at: new Date().toISOString(),
          // only store result if it exists and job is terminal
          result: (isTerminal(status) && sseState?.result !== undefined) ? sseState.result : existing.result,
        };

        // If SSE is in repeated error state, switch to polling fallback
        const errorCount = Number.isFinite(sseState?.errorCount) ? Number(sseState.errorCount) : 0;
        const conn = sseState?.connectionStatus;
        if (!isTerminal(status) && (conn === 'error' && errorCount >= 5)) {
          startPollingFallback(jobId);
        }

        // If terminal, mark for cleanup (don't call stopSSEMonitoring here to avoid loop)
        if (isTerminal(status)) {
          stopPollingFallback(jobId);
          jobsToCleanup.push(jobId);
          ActiveProcessesRegistry.unregister(jobId);
        }
      }

      // Only return new state if there were actual changes
      return hasChanges ? next : prev;
    });

    // Cleanup terminal jobs after state update (outside setState to avoid loop)
    if (jobsToCleanup.length > 0) {
      // Use setTimeout to defer cleanup and avoid triggering useEffect again
      setTimeout(() => {
        for (const jobId of jobsToCleanup) {
          try {
            stopSSEMonitoring(jobId);
          } catch {
            // ignore
          }
        }
      }, 0);
    }

    // Handle job_not_found SSE event: auto-dismiss so the job won't reappear on refresh
    const jobsNotFound: string[] = [];
    for (const [jobId, sseState] of sseJobs.entries()) {
      if ((sseState as any)?.messageType === 'job_not_found') {
        jobsNotFound.push(jobId);
      }
    }
    if (jobsNotFound.length > 0) {
      setTimeout(() => {
        for (const jobId of jobsNotFound) {
          dismissJob(jobId);
        }
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseJobs]);

  // Auto-dismiss terminal jobs after AUTO_DISMISS_MS to keep the UI clean.
  const AUTO_DISMISS_MS = 2 * 60 * 1000; // 2 minutes

  const scheduleAutoDismiss = useCallback((jobId: string) => {
    if (autoDismissRef.current.has(jobId)) return;
    const timer = window.setTimeout(() => {
      autoDismissRef.current.delete(jobId);
      setState((prev) => {
        const nextJobs = { ...prev.jobs };
        delete nextJobs[jobId];
        return { ...prev, jobs: nextJobs };
      });
    }, AUTO_DISMISS_MS);
    autoDismissRef.current.set(jobId, timer);
  }, []);

  useEffect(() => {
    for (const job of Object.values(state.jobs)) {
      if (isTerminal(job.status) && !autoDismissRef.current.has(job.job_id)) {
        scheduleAutoDismiss(job.job_id);
      }
    }
  }, [state.jobs, scheduleAutoDismiss]);

  // Periodic in-session prune to clean up stale jobs
  useEffect(() => {
    const pruneInterval = window.setInterval(() => {
      setState((prev) => JobCenterStorage.prune(prev));
    }, 60_000);
    return () => window.clearInterval(pruneInterval);
  }, []);

  // Cleanup auto-dismiss timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of autoDismissRef.current.values()) {
        window.clearTimeout(timer);
      }
      autoDismissRef.current.clear();
    };
  }, []);

  const open = useCallback((_reason?: string) => {
    setState((prev) => ({ ...prev, is_open: true }));
  }, []);

  const close = useCallback((userInitiated?: boolean) => {
    setState((prev) => ({
      ...prev,
      is_open: false,
      user_closed_at: userInitiated ? new Date().toISOString() : prev.user_closed_at,
    }));
  }, []);

  const getJob = useCallback((job_id: string) => {
    return state.jobs[job_id] || null;
  }, [state.jobs]);

  const dismissJob = useCallback((job_id: string) => {
    stopPollingFallback(job_id);
    ActiveProcessesRegistry.unregister(job_id);
    const adTimer = autoDismissRef.current.get(job_id);
    if (adTimer) { window.clearTimeout(adTimer); autoDismissRef.current.delete(job_id); }
    try {
      stopSSEMonitoring(job_id);
    } catch {
      // ignore
    }
    // Persist dismissed ID so discoverActiveJobsFromBackend skips it on next boot
    dismissedJobIds.current = new Set(dismissedJobIds.current).add(job_id);
    JobCenterStorage.saveDismissed(dismissedJobIds.current);
    setState((prev) => {
      const nextJobs = { ...prev.jobs };
      delete nextJobs[job_id];
      return { ...prev, jobs: nextJobs };
    });
  }, [stopSSEMonitoring]);

  const registerJob = useCallback((input: RegisterJobInput) => {
    const nowIso = new Date().toISOString();
    const jobId = String(input.job_id || '').trim();
    if (!jobId) return;

    const title = String(input.title || '').trim() || 'Job';
    const job_type = input.job_type ? String(input.job_type) : 'unknown';
    const agent_id = typeof input.agent_id === 'number' ? input.agent_id : null;
    const origin = String(input.origin || window.location.pathname || 'unknown');
    const autoOpen = input.auto_open !== false;

    setState((prev) => {
      const existing = prev.jobs[jobId];
      const next: JobCenterState = {
        ...prev,
        jobs: {
          ...prev.jobs,
          [jobId]: {
            job_id: jobId,
            title,
            job_type,
            agent_id,
            origin,
            status: existing?.status || 'created',
            progress: Number.isFinite(existing?.progress) ? existing!.progress : 0,
            step: existing?.step || 'created',
            message: existing?.message || 'Job created',
            created_at: existing?.created_at || nowIso,
            last_updated_at: nowIso,
            transport: existing?.transport || 'sse',
          },
        },
      };
      return JobCenterStorage.prune(next);
    });

    // Prevent inactivity logout while this job is running
    ActiveProcessesRegistry.register(jobId, job_type, { title, origin });

    // Standard notifications
    toastInfo('Job started', title);

    // Standard popup policy: open once if closed; if user closed manually, still open for a NEW job
    if (autoOpen) {
      setState((prev) => {
        if (prev.is_open) return prev;
        return { ...prev, is_open: true };
      });
    }

    startTracking(jobId, { silent: true });
  }, []);

  const startTracking = (jobId: string, opts?: { silent?: boolean }) => {
    // Start SSE monitoring; the hook handles reconnection.
    try {
      startSSEMonitoring(
        jobId,
        undefined,
        (finalState) => {
          const status = normalizeStatus(finalState?.status);
          if (status === 'completed') toastSuccess('Job completed', state.jobs[jobId]?.title || 'Job');
          if (status === 'failed') toastError('Job failed', state.jobs[jobId]?.title || 'Job');
        },
      );
    } catch (e) {
      if (!opts?.silent) toastError('Failed to start job monitoring', String((e as any)?.message || e));
      startPollingFallback(jobId);
    }
  };

  const startPollingFallback = (jobId: string) => {
    if (pollersRef.current.has(jobId)) return;

    // Mark transport as poll
    setState((prev) => {
      const j = prev.jobs[jobId];
      if (!j) return prev;
      return { ...prev, jobs: { ...prev.jobs, [jobId]: { ...j, transport: 'poll' } } };
    });

    const poll = async () => {
      try {
        const status = await apiCall(`jobs/v5/status/${jobId}`, 'GET', null, false, { silentError: true, isPolling: true });
        const st = normalizeStatus(status?.status || status?.state?.status);
        const progress = clampProgress(status?.progress ?? status?.state?.progress);
        const step = (status?.step || status?.state?.step) ?? null;
        const message = (status?.message || status?.state?.message) ?? null;
        const noResult = status?.execution_result_available === false;
        const created =
          status?.created_at ?? status?.state?.created_at ?? status?.metadata?.created_at;
        const ageMin = created ? (Date.now() - new Date(created).getTime()) / 60000 : 0;

        // Stop tracking stale "created" / "pending_retry" jobs with no result (avoids poll loop)
        if ((st === 'created' || st === 'pending_retry') && noResult && ageMin > 15) {
          setState((prev) => {
            const next = { ...prev, jobs: { ...prev.jobs } };
            delete next.jobs[jobId];
            return next;
          });
          stopPollingFallback(jobId);
          ActiveProcessesRegistry.unregister(jobId);
          try {
            stopSSEMonitoring(jobId);
          } catch {
            /* ignore */
          }
          return;
        }

        setState((prev) => {
          const j = prev.jobs[jobId];
          if (!j) return prev;
          const nextJob: JobCenterJob = {
            ...j,
            status: st,
            progress,
            step: typeof step === 'string' ? step : j.step,
            message: typeof message === 'string' ? message : j.message,
            last_updated_at: new Date().toISOString(),
            transport: 'poll',
          };
          return { ...prev, jobs: { ...prev.jobs, [jobId]: nextJob } };
        });

        unauthCountRef.current.delete(jobId);

        if (isTerminal(st)) {
          stopPollingFallback(jobId);
          ActiveProcessesRegistry.unregister(jobId);
        }
      } catch (e: any) {
        const msg = String(e?.message || e || '');
        const lower = msg.toLowerCase();

        // Generic non-recoverable cases: stop tracking to avoid infinite "Connecting..."
        const isNotFound =
          msg.includes('Error 404:') ||
          lower.includes('not found in any storage') ||
          lower.includes('job') && lower.includes('not found');

        const isUnauthorized =
          msg.includes('Error 401:') ||
          msg.includes('Error 403:') ||
          lower.includes('unauthorized') ||
          lower.includes('forbidden');

        if (isNotFound) {
          const terminalMessage = 'Job state is not available. It may have expired, been cleaned up, or the server restarted.';
          unauthCountRef.current.delete(jobId);
          setState((prev) => {
            const j = prev.jobs[jobId];
            if (!j) return prev;
            return {
              ...prev,
              jobs: {
                ...prev.jobs,
                [jobId]: {
                  ...j,
                  status: 'failed',
                  step: 'not_found',
                  message: terminalMessage,
                  last_updated_at: new Date().toISOString(),
                  transport: 'poll',
                },
              },
            };
          });
          stopPollingFallback(jobId);
          ActiveProcessesRegistry.unregister(jobId);
          try { stopSSEMonitoring(jobId); } catch { /* ignore */ }
          toastError('Job tracking stopped', terminalMessage);
          return;
        }

        if (isUnauthorized) {
          const prevCount = unauthCountRef.current.get(jobId) || 0;
          const nextCount = prevCount + 1;
          const UNAUTH_THRESHOLD = 3;
          if (nextCount < UNAUTH_THRESHOLD) {
            unauthCountRef.current.set(jobId, nextCount);
            return;
          }
          const terminalMessage = 'Not authorized to access this job. Please sign in again or verify permissions.';
          unauthCountRef.current.delete(jobId);
          setState((prev) => {
            const j = prev.jobs[jobId];
            if (!j) return prev;
            return {
              ...prev,
              jobs: {
                ...prev.jobs,
                [jobId]: {
                  ...j,
                  status: 'failed',
                  step: 'unauthorized',
                  message: terminalMessage,
                  last_updated_at: new Date().toISOString(),
                  transport: 'poll',
                },
              },
            };
          });
          stopPollingFallback(jobId);
          ActiveProcessesRegistry.unregister(jobId);
          try { stopSSEMonitoring(jobId); } catch { /* ignore */ }
          toastError('Job tracking stopped', terminalMessage);
          return;
        }

        // keep polling silently; SSE may still be trying too (transient errors)
      }
    };

    // Start now and then every 10 seconds (SSE is the primary transport;
    // this is only the fallback when the stream is unavailable)
    void poll();
    const timerId = window.setInterval(poll, 10000);
    pollersRef.current.set(jobId, timerId);
  };

  const stopPollingFallback = (jobId: string) => {
    const timerId = pollersRef.current.get(jobId);
    if (timerId) {
      window.clearInterval(timerId);
      pollersRef.current.delete(jobId);
    }
    unauthCountRef.current.delete(jobId);
  };

  const jobs = useMemo(() => Object.values(state.jobs), [state.jobs]);
  const activeJobs = useMemo(() => jobs.filter((j) => !isTerminal(j.status)), [jobs]);

  const apiValue: JobCenterApi = useMemo(
    () => ({
      state,
      jobs,
      activeJobs,
      registerJob,
      dismissJob,
      open,
      close,
      getJob,
    }),
    [activeJobs, close, dismissJob, getJob, jobs, open, registerJob, state],
  );

  // Ensure apiValue is always defined before rendering Provider
  if (!apiValue) {
    // This should never happen, but provide a fallback to prevent crashes
    console.error('JobCenterProvider: apiValue is undefined');
    return <>{children}</>;
  }

  return <JobCenterContext.Provider value={apiValue}>{children}</JobCenterContext.Provider>;
}

export function useJobCenter(): JobCenterApi {
  const ctx = useContext(JobCenterContext);
  if (!ctx) {
    throw new Error('useJobCenter must be used within JobCenterProvider');
  }
  return ctx;
}

function isTerminal(status: JobV5Status): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

function normalizeStatus(raw: any): JobV5Status {
  const v = String(raw || '').toLowerCase();
  if (!v) return 'unknown';
  if (v === 'completed') return 'completed';
  if (v === 'failed') return 'failed';
  if (v === 'cancelled') return 'cancelled';
  if (v === 'created') return 'created';
  if (v === 'processing') return 'processing';
  if (v === 'in_progress') return 'in_progress';
  if (v === 'running') return 'running';
  // A browser run waiting on a person (hitl_pause writes status=paused with
  // the question in `message`). Used to fall to 'unknown'.
  if (v === 'paused') return 'paused';
  if (v === 'pending_retry') return 'pending_retry';
  return 'unknown';
}

function clampProgress(raw: any): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}


